import { useEffect, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Context
const AuthContext = createContext(null);
const CartContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useCart = () => useContext(CartContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Pages
import Home from "@/pages/Home";
import Tienda from "@/pages/Tienda";
import ProductDetail from "@/pages/ProductDetail";
import Carrito from "@/pages/Carrito";
import Comparador from "@/pages/Comparador";
import Calculadora from "@/pages/Calculadora";
import MiCuenta from "@/pages/MiCuenta";
import Admin from "@/pages/Admin";
import Registro from "@/pages/Registro";
import Login from "@/pages/Login";
import Propuesta from "@/pages/Propuesta";

// Components
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileNav from "@/components/MobileNav";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import CompareBar from "@/components/CompareBar";

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (userData) => {
    const response = await api.post("/auth/register", userData);
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
      setUser(null);
      toast.success("Sesión cerrada");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, register, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// Cart Provider
function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [compareList, setCompareList] = useState([]);
  const [sessionId] = useState(() => localStorage.getItem('cart_session') || `sess_${Date.now()}`);

  useEffect(() => {
    localStorage.setItem('cart_session', sessionId);
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await api.get(`/cart?session_id=${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      const response = await api.post(`/cart/add?session_id=${sessionId}`, {
        product_id: productId,
        quantity
      });
      setCart(response.data);
      toast.success("Producto agregado al carrito");
    } catch (error) {
      toast.error("Error al agregar producto");
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      const response = await api.put(`/cart/update?session_id=${sessionId}`, {
        product_id: productId,
        quantity
      });
      setCart(response.data);
    } catch (error) {
      toast.error("Error al actualizar carrito");
    }
  };

  const clearCart = async () => {
    try {
      await api.delete(`/cart/clear?session_id=${sessionId}`);
      setCart({ items: [], total: 0 });
    } catch (error) {
      toast.error("Error al vaciar carrito");
    }
  };

  const addToCompare = (product) => {
    if (compareList.length >= 4) {
      toast.error("Máximo 4 productos para comparar");
      return;
    }
    if (compareList.find(p => p.product_id === product.product_id)) {
      toast.info("Producto ya está en comparación");
      return;
    }
    setCompareList([...compareList, product]);
    toast.success("Producto agregado a comparación");
  };

  const removeFromCompare = (productId) => {
    setCompareList(compareList.filter(p => p.product_id !== productId));
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      updateCartItem,
      clearCart,
      fetchCart,
      compareList,
      addToCompare,
      removeFromCompare,
      clearCompare,
      itemCount: cart.items?.reduce((acc, item) => acc + item.quantity, 0) || 0
    }}>
      {children}
    </CartContext.Provider>
  );
}

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.4
};

// Animated page wrapper
function AnimatedPage({ children }) {
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      {children}
    </motion.div>
  );
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Admin has its own layout, render without site header/footer
  if (location.pathname.startsWith('/admin')) {
    return <Admin />;
  }

  // Propuesta has its own layout
  if (location.pathname === '/propuesta') {
    return <Propuesta />;
  }

  return (
    <>
      <Header />
      <main className="min-h-screen pb-20 md:pb-0">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<AnimatedPage><Home /></AnimatedPage>} />
            <Route path="/tienda" element={<AnimatedPage><Tienda /></AnimatedPage>} />
            <Route path="/producto/:productId" element={<AnimatedPage><ProductDetail /></AnimatedPage>} />
            <Route path="/carrito" element={<AnimatedPage><Carrito /></AnimatedPage>} />
            <Route path="/comparador" element={<AnimatedPage><Comparador /></AnimatedPage>} />
            <Route path="/calculadora" element={<AnimatedPage><Calculadora /></AnimatedPage>} />
            <Route path="/mi-cuenta" element={<AnimatedPage><MiCuenta /></AnimatedPage>} />
            <Route path="/registro" element={<AnimatedPage><Registro /></AnimatedPage>} />
            <Route path="/login" element={<AnimatedPage><Login /></AnimatedPage>} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
      <MobileNav />
      <WhatsAppFloat />
      <CompareBar />
    </>
  );
}

function App() {
  useEffect(() => {
    // Seed data on first load
    api.post("/seed").catch(() => {});
  }, []);

  return (
    <div className="App font-body">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRouter />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { api, API, BACKEND_URL };
